<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

export type TDropdownItem = {
  label: string;
  action: () => void;
  iconName?: string; // TODO: implement when Icon component is ready
  disabled?: boolean;
  className?: string;
};

const props = defineProps<{
  items: TDropdownItem[];
}>();

const isOpen = ref(false);
const container = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

function toggle() {
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
}

function select(item: TDropdownItem) {
  if (item.disabled) return;

  item.action();
  close();
}

function updatePosition() {
  if (!container.value) return;
  const rect = container.value.getBoundingClientRect();
  menuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom}px`,
    right: `${window.innerWidth - rect.right}px`,
    minWidth: `${rect.width}px`,
  };
}

function onClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (!isOpen.value) return;
  if (container.value?.contains(target) || menuRef.value?.contains(target)) return;

  close();
}

watch(isOpen, (val) => {
  if (val) {
    updatePosition();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
  } else {
    window.removeEventListener('scroll', close, true);
    window.removeEventListener('resize', close);
  }
});

onMounted(() => {
  document.addEventListener('mousedown', onClickOutside);
  if (menuRef.value) document.body.appendChild(menuRef.value);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutside);
  window.removeEventListener('scroll', close, true);
  window.removeEventListener('resize', close);
  menuRef.value?.remove();
});
</script>

<template>
  <div ref="container" class="tw:w-full tw:text-primary" data-testid="dropdown">
    <div @click.prevent="toggle">
      <slot :isOpen="isOpen" />
    </div>

    <div
      ref="menuRef"
      v-show="isOpen"
      :style="menuStyle"
      class="tw:min-w-54 tw:max-h-91 tw:overflow-y-auto tw:py-1 tw:bg-white tw:z-50 tw:rounded-lg tw:shadow-m tw:select-none"
    >
      <div
        v-for="(item, index) in props.items"
        :key="index"
        :class="[
          'tw:py-3 tw:px-4 tw:lg:hover:bg-pr-grey100 tw:active:bg-pr-grey300 tw:typography-control tw:cursor-pointer tw:whitespace-nowrap tw:transition-colors',
          item.className,
        ]"
        @click.prevent="select(item)"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>
